import type { CorrectionResponse } from "@api/types";
import { useSubmitCorrection } from "@corrections/correctionsQueries";
import { useScreenContext } from "@corrections/useScreenContext";
import { Button, Dialog, Textarea } from "@scribenote/brian-design-system";
import { useEffect, useRef, useState } from "react";

export const CorrectionModal = ({
  isOpen,
  onClose,
  onSent,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSent: (response: CorrectionResponse, text: string, sentAt: Date) => void;
}) => {
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const correction = useSubmitCorrection();
  const { patientUuid, visitUuid, patientName } = useScreenContext();

  // A native <dialog> never unmounts, so reset the draft and send state on each open.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setText("");
  }
  // Only on open, never on a send settling: resetting after a failed POST
  // would clear the inline error. The dialog cannot close while a send is in
  // flight, so an open never detaches an in-flight send's observer.
  const resetSend = correction.reset;
  const sending = correction.isPending;
  useEffect(() => {
    if (isOpen) resetSend();
  }, [isOpen, resetSend]);

  // showModal() picks its own focus target, so reclaim it one frame later.
  useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => textareaRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  const canSend = text.trim().length > 0 && !sending;
  const send = () => {
    if (!canSend) return;
    const trimmed = text.trim();
    correction.mutate(
      { text: trimmed, patient_uuid: patientUuid, visit_uuid: visitUuid },
      {
        onSuccess: (response) => {
          onSent(response, trimmed, new Date());
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      // The POST can take 25 s; closing mid-send would drop the reply.
      dismissable={!sending}
      title="Correct Brian"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={send} disabled={!canSend} busy={sending}>
            {sending ? "Sending" : "Send to Brian"}
          </Button>
        </>
      }
    >
      <div className="mx-auto flex max-w-[38rem] flex-col gap-3">
        <p className="text-sm text-annotation">
          {patientName
            ? `About ${patientName}`
            : "Brian will work out what this is about"}
        </p>
        <Textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="What did Brian get wrong? e.g. the dose was 20 mg, not 10"
          minRows={4}
          maxRows={12}
          disabled={sending}
          error={
            correction.isError
              ? "Couldn't send, and your text is kept. Try again."
              : undefined
          }
        />
      </div>
    </Dialog>
  );
};
